/*
 * tq-reviews-link — makes the star rating next to the product title jump down to
 * the reviews.
 *
 * Why this file exists
 * --------------------
 * Two unrelated pieces render the reviews on a product page:
 *
 *   1. The stars + "(6)" under the title are the THEME's own block
 *      (sections/main-product.liquid, `{%- when 'rating' -%}`), fed by the standard
 *      `product.metafields.reviews.*` metafields that Judge.me writes to.
 *      Judge.me's own preview badge is hidden on the product template
 *      (`.jdgm-preview-badge[data-template="product"]{display:none}` in the app's
 *      settings stylesheet), so this is the only star display there.
 *   2. The review list itself is a Judge.me app block, and where it sits depends on
 *      how the merchant placed it in the theme editor — on the live theme it is in
 *      its own "apps" section (`shopify-section-template--…__1784467161b69d1b52`),
 *      on the dev theme it sits inside the product section, after the description.
 *      Either way the id is generated by the admin, so Liquid in main-product.liquid
 *      cannot render a working `href` on its own.
 *
 * So the Liquid renders a placeholder `href="#tq-reviews"` and this script rewrites
 * it to the real id, then upgrades the jump to a smooth scroll. If the Judge.me
 * block is gone (app removed), the anchor is demoted back to plain text so nothing
 * looks clickable for no reason.
 *
 * Load order note: this runs with `defer`, so the whole document is parsed before it
 * executes — the app block's wrapper and `#judgeme_product_reviews` are both
 * server-rendered, long before Judge.me's own script fills the widget in.
 */

/* `#judgeme_product_reviews` is emitted by every Judge.me placement we have; the
   rest are fallbacks for other embeds of the same widget. */
const TQ_REVIEWS_SELECTORS = [
  '#judgeme_product_reviews',
  '.jdgm-review-widget',
  '.jdgm-rev-widg',
  '[id*="judge_me_reviews_review_widget"]',
];

function tqFindReviewsTarget() {
  for (const selector of TQ_REVIEWS_SELECTORS) {
    const node = document.querySelector(selector);
    // Climb to the app-block wrapper (a tight wrapper around the widget in both
    // placements) but NOT to `.shopify-section`: when the block lives inside the
    // product section that would scroll back up to the product itself.
    if (node) return node.closest('.shopify-app-block') || node;
  }
  return null;
}

function tqInitReviewsLink() {
  const links = document.querySelectorAll('.rating-wrapper--link');
  if (!links.length) return;

  const target = tqFindReviewsTarget();

  if (!target) {
    links.forEach((link) => {
      link.removeAttribute('href');
      link.removeAttribute('title');
      link.classList.remove('rating-wrapper--link');
    });
    return;
  }

  // App-block wrappers already carry an id; only bare widgets need one.
  if (!target.id) target.id = 'tq-reviews';
  target.classList.add('tq-reviews-target');

  links.forEach((link) => link.setAttribute('href', `#${target.id}`));
}

document.addEventListener('click', (event) => {
  const link = event.target.closest('.rating-wrapper--link[href^="#"]');
  if (!link) return;

  const target = document.getElementById(link.getAttribute('href').slice(1));
  if (!target) return;

  event.preventDefault();

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

  // Move focus with the viewport so keyboard and screen-reader users land on the
  // reviews too — preventScroll avoids a second, instant jump.
  target.setAttribute('tabindex', '-1');
  target.focus({ preventScroll: true });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tqInitReviewsLink);
} else {
  tqInitReviewsLink();
}
